import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  LogarithmicScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler, // Register Filler plugin
  annotationPlugin // Register annotation plugin
);

export function CrossEntropyGraph() {
  return (
    <figure>
      <figcaption>Cross-Entropy Validation Loss</figcaption>
      <Line options={options} data={getData()} />
    </figure>
  );
}

const options = {
  responsive: true,
  tension: 0.3,
  scales: {
    x: {
      display: true,
    },
    y: {
      display: true,
      type: "logarithmic" as const,
    },
  },
  plugins: {
    annotation: {
      annotations: [
        {
          type: "line" as const,
          xMin: 70,
          xMax: 70,
          borderColor: "rgba(110 110 110 / 75%)",
          borderWidth: 1,
        },
      ],
    },
  },
};

function getData() {
  const labelsDict: { [key: number]: boolean } = {};
  // Add the x values to the labels dictionary.
  for (const dataset of datasets) {
    for (let i = 0; i < dataset.length; i++) {
      labelsDict[dataset[i].x] = true;
    }
  }
  // Extract the labels from the dictionary.
  let labels: number[] = Object.keys(labelsDict).map((x) => parseInt(x));
  // Sort the labels.
  labels.sort((a, b) => a - b);

  // Cut off labels after 1200.
  labels = labels.filter((x) => x <= 1000);

  // Cut off datasets after 1200.
  for (let i = 0; i < datasets.length; i++) {
    datasets[i] = datasets[i].filter((point) => point.x <= 1000);
  }

  return {
    labels: labels,
    datasets: [
      {
        label: "Original",
        data: datasets[1],
        borderColor: "rgba(240 50 50 / 75%)",
        backgroundColor: "rgba(240 50 50 / 75%)",
        pointRadius: 1,
        pointHitRadius: 10,
        fill: false,
      },
      {
        label: "Updated",
        data: datasets[3],
        borderColor: "rgba(90 165 255 / 75%)",
        backgroundColor: "rgba(90 165 255 / 75%)",
        pointRadius: 1,
        pointHitRadius: 10,
        fill: false,
      },
    ],
  };
}

const datasets = [
  [
    { x: 0, y: 4.2638 },
    { x: 10, y: 3.3543 },
    { x: 20, y: 2.9462 },
    { x: 30, y: 2.6413 },
    { x: 40, y: 2.5779 },
    { x: 50, y: 2.503 },
    { x: 60, y: 2.4851 },
    { x: 70, y: 2.4633 },
    { x: 80, y: 2.4533 },
    { x: 90, y: 2.4149 },
    { x: 100, y: 2.4207 },
    { x: 110, y: 2.3765 },
    { x: 120, y: 2.3638 },
    { x: 130, y: 2.316 },
    { x: 140, y: 2.2723 },
    { x: 150, y: 2.2706 },
    { x: 160, y: 2.2119 },
    { x: 170, y: 2.1496 },
    { x: 180, y: 2.1271 },
    { x: 190, y: 2.0549 },
    { x: 200, y: 2.0339 },
    { x: 210, y: 1.9544 },
    { x: 220, y: 1.9502 },
    { x: 230, y: 1.8558 },
    { x: 240, y: 1.8815 },
    { x: 250, y: 1.8248 },
    { x: 260, y: 1.8006 },
    { x: 270, y: 1.7433 },
    { x: 280, y: 1.6879 },
    { x: 290, y: 1.6866 },
    { x: 300, y: 1.7131 },
    { x: 310, y: 1.6598 },
    { x: 320, y: 1.6371 },
    { x: 330, y: 1.602 },
    { x: 340, y: 1.5959 },
    { x: 350, y: 1.5876 },
    { x: 360, y: 1.5764 },
    { x: 370, y: 1.5374 },
    { x: 380, y: 1.5203 },
    { x: 390, y: 1.4431 },
    { x: 400, y: 1.5233 },
    { x: 410, y: 1.511 },
    { x: 420, y: 1.4536 },
    { x: 430, y: 1.4825 },
    { x: 440, y: 1.3924 },
    { x: 450, y: 1.4425 },
    { x: 460, y: 1.4091 },
    { x: 470, y: 1.456 },
    { x: 480, y: 1.4123 },
    { x: 490, y: 1.4124 },
    { x: 500, y: 1.3394 },
    { x: 510, y: 1.4097 },
    { x: 520, y: 1.3697 },
    { x: 530, y: 1.3874 },
    { x: 540, y: 1.3706 },
    { x: 550, y: 1.4172 },
    { x: 560, y: 1.3549 },
    { x: 570, y: 1.3387 },
    { x: 580, y: 1.3269 },
    { x: 590, y: 1.3202 },
    { x: 600, y: 1.323 },
    { x: 610, y: 1.3292 },
    { x: 620, y: 1.2612 },
    { x: 630, y: 1.223 },
    { x: 640, y: 1.276 },
    { x: 650, y: 1.302 },
    { x: 660, y: 1.3183 },
    { x: 670, y: 1.26 },
    { x: 680, y: 1.2373 },
    { x: 690, y: 1.2913 },
    { x: 700, y: 1.2596 },
    { x: 710, y: 1.2505 },
    { x: 720, y: 1.2212 },
    { x: 730, y: 1.2174 },
    { x: 740, y: 1.2041 },
    { x: 750, y: 1.2175 },
    { x: 760, y: 1.2123 },
    { x: 770, y: 1.1875 },
    { x: 780, y: 1.2113 },
    { x: 790, y: 1.199 },
    { x: 800, y: 1.1806 },
    { x: 810, y: 1.2029 },
    { x: 820, y: 1.2087 },
    { x: 830, y: 1.1341 },
    { x: 840, y: 1.1466 },
    { x: 850, y: 1.1429 },
    { x: 860, y: 1.1069 },
    { x: 870, y: 1.1558 },
    { x: 880, y: 1.0904 },
    { x: 890, y: 1.1294 },
    { x: 900, y: 1.113 },
    { x: 910, y: 1.1112 },
    { x: 920, y: 1.105 },
    { x: 930, y: 1.1111 },
    { x: 940, y: 1.098 },
    { x: 950, y: 1.0493 },
    { x: 960, y: 1.0903 },
    { x: 970, y: 1.0495 },
    { x: 980, y: 1.0821 },
    { x: 990, y: 1.0787 },
    { x: 1000, y: 1.0479 },
    { x: 1010, y: 1.0679 },
    { x: 1020, y: 1.0466 },
    { x: 1030, y: 1.0151 },
    { x: 1040, y: 1.0381 },
    { x: 1050, y: 1.0449 },
    { x: 1060, y: 1.0527 },
    { x: 1070, y: 1.056 },
    { x: 1080, y: 0.9661 },
    { x: 1090, y: 0.9654 },
    { x: 1100, y: 1.0079 },
    { x: 1110, y: 0.9654 },
    { x: 1120, y: 0.9359 },
    { x: 1130, y: 0.9466 },
    { x: 1140, y: 0.9903 },
    { x: 1150, y: 0.9519 },
    { x: 1160, y: 0.9425 },
    { x: 1170, y: 0.9219 },
    { x: 1180, y: 0.9039 },
    { x: 1190, y: 0.9382 },
    { x: 1200, y: 0.8972 },
    { x: 1210, y: 0.9227 },
    { x: 1220, y: 0.8762 },
    { x: 1230, y: 0.8759 },
    { x: 1240, y: 0.8467 },
    { x: 1250, y: 0.8711 },
    { x: 1260, y: 0.8275 },
    { x: 1270, y: 0.8639 },
    { x: 1280, y: 0.8358 },
    { x: 1290, y: 0.8323 },
    { x: 1300, y: 0.8669 },
    { x: 1310, y: 0.8295 },
    { x: 1320, y: 0.7714 },
    { x: 1330, y: 0.7751 },
    { x: 1340, y: 0.8015 },
    { x: 1350, y: 0.8274 },
    { x: 1360, y: 0.7578 },
    { x: 1370, y: 0.769 },
    { x: 1380, y: 0.7385 },
    { x: 1390, y: 0.747 },
    { x: 1400, y: 0.7587 },
    { x: 1410, y: 0.7099 },
    { x: 1420, y: 0.754 },
    { x: 1430, y: 0.6999 },
    { x: 1440, y: 0.6731 },
    { x: 1450, y: 0.6625 },
    { x: 1460, y: 0.6695 },
    { x: 1470, y: 0.6623 },
    { x: 1480, y: 0.6538 },
    { x: 1490, y: 0.6644 },
    { x: 1500, y: 0.6836 },
    { x: 1510, y: 0.6345 },
    { x: 1520, y: 0.6241 },
    { x: 1530, y: 0.6107 },
    { x: 1540, y: 0.5951 },
    { x: 1550, y: 0.58 },
    { x: 1560, y: 0.598 },
    { x: 1570, y: 0.5719 },
    { x: 1580, y: 0.4863 },
    { x: 1590, y: 0.5596 },
    { x: 1600, y: 0.5792 },
    { x: 1610, y: 0.5114 },
    { x: 1620, y: 0.5716 },
    { x: 1630, y: 0.518 },
    { x: 1640, y: 0.5116 },
    { x: 1650, y: 0.5382 },
    { x: 1660, y: 0.5063 },
    { x: 1670, y: 0.5196 },
    { x: 1680, y: 0.4824 },
    { x: 1690, y: 0.4786 },
    { x: 1700, y: 0.4884 },
    { x: 1710, y: 0.4533 },
    { x: 1720, y: 0.4599 },
    { x: 1730, y: 0.4613 },
    { x: 1740, y: 0.4955 },
    { x: 1750, y: 0.4233 },
    { x: 1760, y: 0.4055 },
    { x: 1770, y: 0.4117 },
    { x: 1780, y: 0.4037 },
    { x: 1790, y: 0.402 },
    { x: 1800, y: 0.4018 },
    { x: 1810, y: 0.3969 },
    { x: 1820, y: 0.3879 },
    { x: 1830, y: 0.3787 },
    { x: 1840, y: 0.4297 },
    { x: 1850, y: 0.41 },
    { x: 1860, y: 0.3864 },
    { x: 1870, y: 0.3593 },
    { x: 1880, y: 0.3073 },
    { x: 1890, y: 0.3152 },
    { x: 1900, y: 0.3108 },
    { x: 1910, y: 0.3686 },
    { x: 1920, y: 0.2966 },
    { x: 1930, y: 0.3294 },
    { x: 1940, y: 0.2959 },
    { x: 1950, y: 0.2925 },
    { x: 1960, y: 0.2832 },
    { x: 1970, y: 0.3039 },
    { x: 1980, y: 0.2786 },
    { x: 1990, y: 0.2992 },
    { x: 2000, y: 0.2572 },
    { x: 2010, y: 0.2711 },
    { x: 2020, y: 0.253 },
    { x: 2030, y: 0.2798 },
    { x: 2040, y: 0.2795 },
    { x: 2050, y: 0.2613 },
    { x: 2060, y: 0.261 },
    { x: 2070, y: 0.272 },
    { x: 2080, y: 0.2313 },
    { x: 2090, y: 0.2512 },
    { x: 2100, y: 0.2361 },
    { x: 2110, y: 0.2283 },
    { x: 2120, y: 0.2413 },
    { x: 2130, y: 0.2413 },
    { x: 2140, y: 0.2115 },
    { x: 2150, y: 0.2138 },
    { x: 2160, y: 0.2166 },
    { x: 2170, y: 0.2365 },
    { x: 2180, y: 0.2121 },
    { x: 2190, y: 0.1961 },
    { x: 2200, y: 0.194 },
    { x: 2210, y: 0.2132 },
    { x: 2220, y: 0.2054 },
    { x: 2230, y: 0.1963 },
    { x: 2240, y: 0.2161 },
    { x: 2250, y: 0.1979 },
    { x: 2260, y: 0.1993 },
    { x: 2270, y: 0.2035 },
    { x: 2280, y: 0.2012 },
    { x: 2290, y: 0.1837 },
    { x: 2300, y: 0.1925 },
    { x: 2310, y: 0.1952 },
    { x: 2320, y: 0.1867 },
    { x: 2330, y: 0.1903 },
    { x: 2340, y: 0.1767 },
    { x: 2350, y: 0.1984 },
    { x: 2360, y: 0.1912 },
    { x: 2370, y: 0.1791 },
    { x: 2380, y: 0.1844 },
    { x: 2390, y: 0.1705 },
    { x: 2400, y: 0.167 },
    { x: 2410, y: 0.1683 },
    { x: 2420, y: 0.1819 },
    { x: 2430, y: 0.1916 },
    { x: 2440, y: 0.1675 },
    { x: 2450, y: 0.1664 },
    { x: 2460, y: 0.1675 },
    { x: 2470, y: 0.1818 },
    { x: 2480, y: 0.1536 },
    { x: 2490, y: 0.1881 },
    { x: 2500, y: 0.1598 },
  ],
  [
    { x: 0, y: 4.260243892669678 },
    { x: 100, y: 2.4413063526153564 },
    { x: 200, y: 2.1050522327423096 },
    { x: 300, y: 1.8319637775421143 },
    { x: 400, y: 1.6851868629455566 },
    { x: 500, y: 1.6137689352035522 },
    { x: 600, y: 1.583006739616394 },
    { x: 700, y: 1.5414150953292847 },
    { x: 800, y: 1.5435724258422852 },
    { x: 900, y: 1.5420390367507935 },
    { x: 1000, y: 1.563615083694458 },
    { x: 1100, y: 1.610456943511963 },
    { x: 1200, y: 1.686582088470459 },
    { x: 1300, y: 1.7655084133148193 },
    { x: 1400, y: 1.862412691116333 },
    { x: 1500, y: 1.9860620498657227 },
    { x: 1600, y: 2.1101934909820557 },
    { x: 1700, y: 2.234762668609619 },
    { x: 1800, y: 2.4075498580932617 },
    { x: 1900, y: 2.5821027755737305 },
    { x: 2000, y: 2.7034614086151123 },
    { x: 2100, y: 2.8240184783935547 },
    { x: 2200, y: 2.956655502319336 },
    { x: 2300, y: 3.068300724029541 },
    { x: 2400, y: 3.1299712657928467 },
    { x: 2500, y: 3.2482411861419678 },
  ],
  [
    { x: 0, y: 4.2638 },
    { x: 10, y: 4.1693 },
    { x: 20, y: 3.9253 },
    { x: 30, y: 3.5942 },
    { x: 40, y: 3.397 },
    { x: 50, y: 3.3132 },
    { x: 60, y: 3.0984 },
    { x: 70, y: 2.8358 },
    { x: 80, y: 2.7581 },
    { x: 90, y: 2.6882 },
    { x: 100, y: 2.6918 },
    { x: 110, y: 2.5454 },
    { x: 120, y: 2.5213 },
    { x: 130, y: 2.494 },
    { x: 140, y: 2.4504 },
    { x: 150, y: 2.4555 },
    { x: 160, y: 2.4439 },
    { x: 170, y: 2.3895 },
    { x: 180, y: 2.3898 },
    { x: 190, y: 2.3457 },
    { x: 200, y: 2.3078 },
    { x: 210, y: 2.2609 },
    { x: 220, y: 2.2688 },
    { x: 230, y: 2.1386 },
    { x: 240, y: 2.1566 },
    { x: 250, y: 2.0908 },
    { x: 260, y: 2.0429 },
    { x: 270, y: 1.9823 },
    { x: 280, y: 1.9034 },
    { x: 290, y: 1.8738 },
    { x: 300, y: 1.8699 },
    { x: 310, y: 1.8338 },
    { x: 320, y: 1.8092 },
    { x: 330, y: 1.757 },
    { x: 340, y: 1.7252 },
    { x: 350, y: 1.705 },
    { x: 360, y: 1.6824 },
    { x: 370, y: 1.6563 },
    { x: 380, y: 1.6308 },
    { x: 390, y: 1.5517 },
    { x: 400, y: 1.6108 },
    { x: 410, y: 1.5955 },
    { x: 420, y: 1.5146 },
    { x: 430, y: 1.5387 },
    { x: 440, y: 1.4859 },
    { x: 450, y: 1.4855 },
    { x: 460, y: 1.4496 },
    { x: 470, y: 1.5078 },
    { x: 480, y: 1.4531 },
    { x: 490, y: 1.4606 },
    { x: 500, y: 1.3751 },
    { x: 510, y: 1.4403 },
    { x: 520, y: 1.4017 },
    { x: 530, y: 1.4049 },
    { x: 540, y: 1.3873 },
    { x: 550, y: 1.4491 },
    { x: 560, y: 1.3951 },
    { x: 570, y: 1.3454 },
    { x: 580, y: 1.3462 },
    { x: 590, y: 1.3507 },
    { x: 600, y: 1.3353 },
    { x: 610, y: 1.3404 },
    { x: 620, y: 1.2761 },
    { x: 630, y: 1.2463 },
    { x: 640, y: 1.2953 },
    { x: 650, y: 1.2972 },
    { x: 660, y: 1.3214 },
    { x: 670, y: 1.2784 },
    { x: 680, y: 1.2341 },
    { x: 690, y: 1.2871 },
    { x: 700, y: 1.2607 },
    { x: 710, y: 1.2525 },
    { x: 720, y: 1.2226 },
    { x: 730, y: 1.2241 },
    { x: 740, y: 1.211 },
    { x: 750, y: 1.2305 },
    { x: 760, y: 1.2153 },
    { x: 770, y: 1.1988 },
    { x: 780, y: 1.2032 },
    { x: 790, y: 1.1983 },
    { x: 800, y: 1.1859 },
    { x: 810, y: 1.204 },
    { x: 820, y: 1.206 },
    { x: 830, y: 1.1324 },
    { x: 840, y: 1.1443 },
    { x: 850, y: 1.1359 },
    { x: 860, y: 1.1153 },
    { x: 870, y: 1.1447 },
    { x: 880, y: 1.0793 },
    { x: 890, y: 1.1149 },
    { x: 900, y: 1.1199 },
    { x: 910, y: 1.1229 },
    { x: 920, y: 1.1167 },
    { x: 930, y: 1.0992 },
    { x: 940, y: 1.0749 },
    { x: 950, y: 1.0394 },
    { x: 960, y: 1.0832 },
    { x: 970, y: 1.0341 },
    { x: 980, y: 1.0782 },
    { x: 990, y: 1.0586 },
    { x: 1000, y: 1.0252 },
    { x: 1010, y: 1.0629 },
    { x: 1020, y: 1.0387 },
    { x: 1030, y: 1.0074 },
    { x: 1040, y: 1.0101 },
    { x: 1050, y: 1.0245 },
    { x: 1060, y: 1.039 },
    { x: 1070, y: 1.0236 },
    { x: 1080, y: 0.9406 },
    { x: 1090, y: 0.9509 },
    { x: 1100, y: 0.9706 },
    { x: 1110, y: 0.9292 },
    { x: 1120, y: 0.9094 },
    { x: 1130, y: 0.9196 },
    { x: 1140, y: 0.9687 },
    { x: 1150, y: 0.9305 },
    { x: 1160, y: 0.9065 },
    { x: 1170, y: 0.8932 },
    { x: 1180, y: 0.8617 },
    { x: 1190, y: 0.9009 },
    { x: 1200, y: 0.8664 },
    { x: 1210, y: 0.8723 },
    { x: 1220, y: 0.8195 },
    { x: 1230, y: 0.8195 },
    { x: 1240, y: 0.7926 },
    { x: 1250, y: 0.7927 },
    { x: 1260, y: 0.7616 },
    { x: 1270, y: 0.7869 },
    { x: 1280, y: 0.7831 },
    { x: 1290, y: 0.7661 },
    { x: 1300, y: 0.7979 },
    { x: 1310, y: 0.7781 },
    { x: 1320, y: 0.6938 },
    { x: 1330, y: 0.6887 },
    { x: 1340, y: 0.7421 },
    { x: 1350, y: 0.7548 },
    { x: 1360, y: 0.6619 },
    { x: 1370, y: 0.6922 },
    { x: 1380, y: 0.6468 },
    { x: 1390, y: 0.6733 },
    { x: 1400, y: 0.6606 },
    { x: 1410, y: 0.6186 },
    { x: 1420, y: 0.6804 },
    { x: 1430, y: 0.5947 },
    { x: 1440, y: 0.5703 },
    { x: 1450, y: 0.5341 },
    { x: 1460, y: 0.5607 },
    { x: 1470, y: 0.5611 },
    { x: 1480, y: 0.5408 },
    { x: 1490, y: 0.5678 },
    { x: 1500, y: 0.5704 },
    { x: 1510, y: 0.5193 },
    { x: 1520, y: 0.5289 },
    { x: 1530, y: 0.506 },
    { x: 1540, y: 0.4767 },
    { x: 1550, y: 0.4663 },
    { x: 1560, y: 0.489 },
    { x: 1570, y: 0.4482 },
    { x: 1580, y: 0.3752 },
    { x: 1590, y: 0.4496 },
    { x: 1600, y: 0.4641 },
    { x: 1610, y: 0.3968 },
    { x: 1620, y: 0.4532 },
    { x: 1630, y: 0.4052 },
    { x: 1640, y: 0.3933 },
    { x: 1650, y: 0.4163 },
    { x: 1660, y: 0.3776 },
    { x: 1670, y: 0.4 },
    { x: 1680, y: 0.3662 },
    { x: 1690, y: 0.3769 },
    { x: 1700, y: 0.3549 },
    { x: 1710, y: 0.354 },
    { x: 1720, y: 0.3222 },
    { x: 1730, y: 0.3463 },
    { x: 1740, y: 0.3746 },
    { x: 1750, y: 0.3066 },
    { x: 1760, y: 0.3041 },
    { x: 1770, y: 0.3016 },
    { x: 1780, y: 0.3113 },
    { x: 1790, y: 0.3021 },
    { x: 1800, y: 0.274 },
    { x: 1810, y: 0.2741 },
    { x: 1820, y: 0.2921 },
    { x: 1830, y: 0.2517 },
    { x: 1840, y: 0.3161 },
    { x: 1850, y: 0.2898 },
    { x: 1860, y: 0.2869 },
    { x: 1870, y: 0.2382 },
    { x: 1880, y: 0.2226 },
    { x: 1890, y: 0.2153 },
    { x: 1900, y: 0.2193 },
    { x: 1910, y: 0.2624 },
    { x: 1920, y: 0.2042 },
    { x: 1930, y: 0.2285 },
    { x: 1940, y: 0.1992 },
    { x: 1950, y: 0.205 },
    { x: 1960, y: 0.1947 },
    { x: 1970, y: 0.2072 },
    { x: 1980, y: 0.1981 },
    { x: 1990, y: 0.2039 },
    { x: 2000, y: 0.1809 },
    { x: 2010, y: 0.1983 },
    { x: 2020, y: 0.1691 },
    { x: 2030, y: 0.1898 },
    { x: 2040, y: 0.1994 },
    { x: 2050, y: 0.1803 },
    { x: 2060, y: 0.178 },
    { x: 2070, y: 0.1898 },
    { x: 2080, y: 0.1652 },
    { x: 2090, y: 0.1776 },
    { x: 2100, y: 0.1546 },
    { x: 2110, y: 0.1603 },
    { x: 2120, y: 0.1754 },
    { x: 2130, y: 0.1654 },
    { x: 2140, y: 0.1519 },
    { x: 2150, y: 0.1477 },
    { x: 2160, y: 0.1601 },
    { x: 2170, y: 0.1594 },
    { x: 2180, y: 0.148 },
    { x: 2190, y: 0.1363 },
    { x: 2200, y: 0.1427 },
    { x: 2210, y: 0.1478 },
    { x: 2220, y: 0.1546 },
    { x: 2230, y: 0.1444 },
    { x: 2240, y: 0.1536 },
    { x: 2250, y: 0.141 },
    { x: 2260, y: 0.1408 },
    { x: 2270, y: 0.1433 },
    { x: 2280, y: 0.1501 },
    { x: 2290, y: 0.1305 },
    { x: 2300, y: 0.1421 },
    { x: 2310, y: 0.1416 },
    { x: 2320, y: 0.134 },
    { x: 2330, y: 0.1332 },
    { x: 2340, y: 0.1341 },
    { x: 2350, y: 0.1458 },
    { x: 2360, y: 0.1341 },
    { x: 2370, y: 0.131 },
    { x: 2380, y: 0.1321 },
    { x: 2390, y: 0.1304 },
    { x: 2400, y: 0.1255 },
    { x: 2410, y: 0.1245 },
    { x: 2420, y: 0.1314 },
    { x: 2430, y: 0.1396 },
    { x: 2440, y: 0.1251 },
    { x: 2450, y: 0.1245 },
    { x: 2460, y: 0.1214 },
    { x: 2470, y: 0.1299 },
    { x: 2480, y: 0.1179 },
    { x: 2490, y: 0.1329 },
    { x: 2500, y: 0.125 },
  ],
  [
    { x: 0, y: 4.260243892669678 },
    { x: 100, y: 2.686936378479004 },
    { x: 200, y: 2.3633687496185303 },
    { x: 300, y: 1.9658969640731812 },
    { x: 400, y: 1.757503867149353 },
    { x: 500, y: 1.6472010612487793 },
    { x: 600, y: 1.5906833410263062 },
    { x: 700, y: 1.554924726486206 },
    { x: 800, y: 1.5689246654510498 },
    { x: 900, y: 1.5574243068695068 },
    { x: 1000, y: 1.598781704902649 },
    { x: 1100, y: 1.6126676797866821 },
    { x: 1200, y: 1.7205702066421509 },
    { x: 1300, y: 1.8277941942214966 },
    { x: 1400, y: 1.9491702318191528 },
    { x: 1500, y: 2.104318857192993 },
    { x: 1600, y: 2.235584020614624 },
    { x: 1700, y: 2.3694708347320557 },
    { x: 1800, y: 2.548095703125 },
    { x: 1900, y: 2.7052502632141113 },
    { x: 2000, y: 2.8126609325408936 },
    { x: 2100, y: 2.9000701904296875 },
    { x: 2200, y: 3.00636625289917 },
    { x: 2300, y: 3.1080994606018066 },
    { x: 2400, y: 3.1246728897094727 },
    { x: 2500, y: 3.210085868835449 },
  ],
];
